import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import GetAssignedBucket from '|/helpers/experiment/services';

export const AUTH_SWAPOVER_EXPERIMENT = 'taf-to-core-auth';
const RUM_CONTEXT_KEY = `experiments.${AUTH_SWAPOVER_EXPERIMENT}`;
// Only core-auth registration waits on this; cancels rather than delaying it indefinitely.
const BUCKET_TIMEOUT_MS = 2000;

interface AuthSwapoverExperimentResult {
  isInTreatment: boolean;
  isExperimentLoading: boolean;
}

// Bucketed by browser, never by account: this resolves before an access token
// exists, so user_id would split one browser across buckets.
function useAuthSwapoverExperiment(): AuthSwapoverExperimentResult {
  const [bucket, setBucket] = useState<string | null>(null);

  useEffect(() => {
    // Without this, a late resolution re-adds the RUM key the cleanup just removed.
    let active = true;

    // Every unavailable bucket runs as control and is logged rather than branched on.
    const fallBackToControl = (reason: string) => {
      if (!active) return;
      datadogRum.addAction('auth-swapover-bucket-unavailable', {
        experimentName: AUTH_SWAPOVER_EXPERIMENT,
        reason
      });
      setBucket('control');
    };

    const identifier = Cookies.get('BrowserCookieId');

    // Skip rather than let the service fall back to user_id.
    if (!identifier) {
      fallBackToControl('no-browser-cookie-id');
      return;
    }

    GetAssignedBucket({
      experimentName: AUTH_SWAPOVER_EXPERIMENT,
      identifier,
      apiTimeout: BUCKET_TIMEOUT_MS,
      cacheMinutes: 15
    })
      .then(result => {
        if (!active) return;

        // Covers a timed-out lookup: the service reports the cancellation as an error.
        if ('hasErrors' in result && result.hasErrors) {
          fallBackToControl('lookup-failed');
          return;
        }

        const assignedBucket = result.assignedBucket ?? 'control';
        setBucket(assignedBucket);
        // Server-sourced experiment — not in ExperimentProvider, requires manual RUM context
        datadogRum.setGlobalContextProperty(RUM_CONTEXT_KEY, assignedBucket);
      })
      .catch(() => {
        fallBackToControl('lookup-rejected');
      });

    return () => {
      active = false;
      datadogRum.removeGlobalContextProperty(RUM_CONTEXT_KEY);
    };
  }, []);

  return {
    isInTreatment: bucket === 'treatment',
    isExperimentLoading: bucket === null
  };
}

export default useAuthSwapoverExperiment;
